import { Component, ElementRef, EventEmitter, Input, OnInit, Output, TemplateRef, ViewContainerRef } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { CourtshipTypeEnum, CourtshipTypeLabels } from '../../enums/courtship-type.enum';
import { CompanyService } from '../../../core/data-services/company';
import { BannerNotificationsService } from '../../../xform-compat';
import { RmsColors } from '../../constants/rms-colors';

@Component({
    selector: 'rms-courtship-selector',
    templateUrl: './courtship-selector.component.html',
    styleUrls: ['./courtship-selector.component.scss'],
    standalone: false
})
export class CourtshipSelectorComponent implements OnInit {

  private static readonly DEBOUNCE_TIME = 500; // ms

  @Input() companyId: number;
  @Input() courtship: CourtshipTypeEnum;
  @Input() seniorAttention: boolean;
  @Input() readOnly = false;
  @Output() onChangeCourtship: EventEmitter<any> = new EventEmitter();
  @Output() onChangeSeniorAttention: EventEmitter<any> = new EventEmitter();

  public readonly courtshipLabels = CourtshipTypeLabels;
  public courtshipAffirmed = false;
  private originalCourtship;

  private courtshipDebouncer: Subject<{ id: number, courtshipType: CourtshipTypeEnum }> =
    new Subject<{ id: number, courtshipType: CourtshipTypeEnum }>();
  private seniorDebouncer: Subject<{ id: number, seniorAttention: boolean }> =
    new Subject<{ id: number, seniorAttention: boolean }>();
  private tooltipElement: HTMLElement;

  constructor(
    private companyService: CompanyService,
    private viewContainerRef: ViewContainerRef,
    private bannerService: BannerNotificationsService,
  ) {
    this.courtshipDebouncer
        .pipe(debounceTime(CourtshipSelectorComponent.DEBOUNCE_TIME))
        .subscribe((value) => {
          this.companyService.updateCompanyCourtship(value.id, value.courtshipType)
            .subscribe(result => {
              if (this.originalCourtship === value.courtshipType.valueOf()) {
                this.bannerService.success('Courtship affirmed');
              }
              this.onChangeCourtship.emit(value);
              this.courtshipAffirmed = true;
            });
        });

    this.seniorDebouncer
      .pipe(debounceTime(CourtshipSelectorComponent.DEBOUNCE_TIME))
      .subscribe((value) => {
        this.companyService.updateCompanySeniorAttention(value.id, value.seniorAttention).subscribe(() => {
          this.onChangeSeniorAttention.emit(value);
        });
      });
   }

  ngOnInit() {
    if (this.courtship) {
      this.originalCourtship = this.courtship.valueOf();
    }
  }


  public getCompanyCourtshipIcon(): string {
    if (this.courtship) {
      return this.courtship === CourtshipTypeEnum.NONE ? 'fa fa-heart-o' : 'fa fa-heart';
    }
    return 'fa fa-heart-o';
  }

  public getSeniorAttentionIcon(): string {
    return this.seniorAttention ? 'crown.svg' : 'crown-o.svg';
  }

  public getCompanyCourtshipColor(): string {
    if (this.courtship === CourtshipTypeEnum.STANDARD) {
      return RmsColors.RMS_DOMAIN_BADGE_COMMERCIAL_FONT;
    }
    return RmsColors.RMS_DIVIDER_COLOR;
  }

  public getSeniorAttentionColor(): string {
    return this.seniorAttention ?
      'brightness(0) saturate(100%) invert(96%) sepia(47%) saturate(6296%) hue-rotate(319deg) brightness(100%) contrast(103%)' :
      'brightness(0) saturate(100%) invert(94%) sepia(5%) saturate(73%) hue-rotate(155deg) brightness(90%) contrast(92%)';
  }

  public handleCourtshipChange() {
    if (!this.readOnly) {
      this.courtship = CourtshipTypeEnum.next(this.courtship);
      this.courtshipDebouncer.next({ id: this.companyId, courtshipType: this.courtship });
    }
  }

  public handleSeniorChange() {
    if (!this.readOnly) {
      this.seniorAttention = !this.seniorAttention;
      this.seniorDebouncer.next({ id: this.companyId, seniorAttention: this.seniorAttention });
    }
  }

  public affirmCourtship() {
    this.courtshipDebouncer.next({ id: this.companyId, courtshipType: this.courtship });
  }

  public showTooltip(tooltipTemplate: TemplateRef<any>, container: HTMLElement) {
    if (!this.tooltipElement) {
      const embeddedViewRef = this.viewContainerRef.createEmbeddedView(tooltipTemplate);
      for (const node of embeddedViewRef.rootNodes) {
        const ele = document.body.appendChild(node) as HTMLElement;
        if (ele.style) {
          this.tooltipElement = ele;
        }
      }
    }

    this.tooltipElement.style.setProperty('position', 'fixed');
    this.tooltipElement.style.setProperty('top',
      `${container.getBoundingClientRect().top - (container.getBoundingClientRect().height / 2)}px`);
    this.tooltipElement.style.setProperty('left',
      `${container.getBoundingClientRect().left + (container.getBoundingClientRect().width / 2)}px`);
    this.tooltipElement.style.setProperty('transform', 'translate(-50%, -100%)');
    this.tooltipElement.style.setProperty('z-index', '1001');
    this.tooltipElement.style.setProperty('visibility', 'visible');
  }

  public hideTooltip() {
    if (this.tooltipElement) {
      this.tooltipElement.style.setProperty('visibility', 'hidden');
    }
  }

}
